<script lang="ts">
  // Breakthrough · Spotlight: the medal front and centre, the signature gains, and the ride
  // with the moment MPA met power.
  import { MEDAL_COLORS, type ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import Medal from '../parts/Medal.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'
  import SignatureDeltas from '../parts/SignatureDeltas.svelte'

  export let ctx: ReportContext

  $: bt = ctx.data.breakthrough!
  $: medal = MEDAL_COLORS[bt.level]
  $: wide = ctx.size === 'landscape'
  $: stats = statsFor(ctx, ['stat.xss', 'stat.difficulty', 'stat.time', 'stat.distance', 'stat.power', 'stat.recovery'])
  $: base = `radial-gradient(90% 55% at ${wide ? '22% 45%' : '50% 30%'}, ${medal.main}33 0%, transparent 70%), radial-gradient(120% 80% at 20% 0%, #172a33 0%, #0b1418 60%, #06090b 100%)`
  $: medalSize = { portrait: 210, square: 150, landscape: 190 }[ctx.size]
</script>

<Frame {ctx} {base} shade="even">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow" style="color: {medal.main};">Breakthrough</span>
      <Brand height={38} />
    </div>

    <div class="xr-title"><TitleBlock {ctx} lines={wide ? 2 : 1} /></div>

    <div class="xr-hero">
      <Medal level={bt.level} size={medalSize} />
      <div class="xr-hero-text">
        <div class="xr-level" style="color: {medal.main};">{bt.level === 'near' ? 'Near' : medal.label}</div>
        <div class="xr-kind">Breakthrough</div>
      </div>
    </div>

    {#if ctx.show('deltas') && bt.delta}
      <div class="xr-deltas"><SignatureDeltas {ctx} /></div>
    {/if}

    <div class="xr-chart">
      {#if ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={120} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} {height} breakthrough={ctx.show('marker') ? bt.level : null} showMpa={ctx.show('mpa')} />
        </Fill>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={wide ? 2 : 4} size="sm" /></div>
    <div class="xr-badges"><Badges {ctx} own="breakthrough" /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas: 'top' 'title' 'hero' 'deltas' 'chart' 'stats' 'badges';
    row-gap: 28px;
  }
  :global(.xr-square) .xr-layout {
    row-gap: 22px;
  }
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 600px minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'title deltas'
      'hero chart'
      'stats chart'
      'badges chart';
    column-gap: 72px;
    row-gap: 30px;
  }
  .xr-top { grid-area: top; display: flex; align-items: center; justify-content: space-between; }
  .xr-title { grid-area: title; min-width: 0; }
  .xr-hero { grid-area: hero; }
  .xr-deltas { grid-area: deltas; }
  .xr-chart { grid-area: chart; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }

  .xr-eyebrow {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 0.32em;
    text-transform: uppercase;
  }
  .xr-hero {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin: 8px 0;
  }
  :global(.xr-landscape) .xr-hero {
    justify-content: flex-start;
    align-self: center;
    gap: 34px;
    margin: 0;
  }
  .xr-hero-text {
    display: flex;
    flex-direction: column;
  }
  .xr-level {
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    font-size: 176px;
    line-height: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.01em;
  }
  :global(.xr-square) .xr-level {
    font-size: 132px;
  }
  :global(.xr-landscape) .xr-level {
    font-size: 150px;
  }
  .xr-kind {
    font-family: var(--display);
    font-weight: 700;
    font-size: 58px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-top: 8px;
  }
  :global(.xr-square) .xr-kind {
    font-size: 46px;
  }
  :global(.xr-landscape) .xr-kind {
    font-size: 48px;
    letter-spacing: 0.12em;
  }
</style>
